<script lang="ts">
  import type { User } from "../../lib/api";
  import Card from "../../lib/ui/Card.svelte";
  import Pill from "../../lib/ui/Pill.svelte";
  import Button from "../../lib/ui/Button.svelte";
  import EmptyState from "../../lib/ui/EmptyState.svelte";
  import ActionMenu from "../../lib/ui/ActionMenu.svelte";

  let {
    title,
    subtitle,
    users,
    tone,
    busyEmail,
    onReset,
    onInvite,
    onDelete,
  }: {
    title: string;
    subtitle: string;
    users: User[];
    tone: "indigo" | "slate";
    busyEmail: string;
    onReset: (email: string) => void;
    onInvite: (email: string) => void;
    onDelete: (email: string) => void;
  } = $props();

  const fmt = (iso: string) => new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const roleLabel = (r: string) => (r === "portal" ? "Portal admin" : "App user");
  // Portal admins (tone indigo) have no org membership by design — only show the
  // Orgs column for the app-users table.
  const showOrgs = $derived(tone === "slate");
</script>

<Card {title} {subtitle} pad={false}>
  {#if users.length === 0}
    <EmptyState icon="◍" title="No {title.toLowerCase()} yet" hint="Add one with the form above." />
  {:else}
    <table class="data-table">
      <thead>
        <tr>
          <th>Email</th>
          <th>Role</th>
          {#if showOrgs}<th>Orgs</th>{/if}
          <th class="whitespace-nowrap">Created</th>
          <th class="text-right!">Actions</th>
        </tr>
      </thead>
      <tbody>
        {#each users as u (u.id)}
          <tr class={busyEmail === u.email ? "opacity-55" : ""}>
            <td class="font-semibold">{u.email}</td>
            <td>
              <div class="flex gap-1.5 flex-wrap items-center">
                <Pill {tone}>{roleLabel(u.role)}</Pill>
                {#if u.pending}<Pill tone="amber">Invite pending</Pill>{/if}
              </div>
            </td>
            {#if showOrgs}
              <td>
                {#if u.orgs && u.orgs.length}
                  <div class="flex gap-1.5 flex-wrap">
                    {#each u.orgs as o (o.name)}
                      <Pill tone="slate">{o.name} · {o.role}</Pill>
                    {/each}
                  </div>
                {:else}
                  <span class="text-muted text-xs">none</span>
                {/if}
              </td>
            {/if}
            <td class="text-muted whitespace-nowrap">{fmt(u.created_at)}</td>
            <td>
              <div class="flex gap-1 justify-end items-center">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busyEmail === u.email}
                  onclick={() => onReset(u.email)}>Reset password</Button
                >
                {#if u.pending}
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busyEmail === u.email}
                    onclick={() => onInvite(u.email)}>Resend invite</Button
                  >
                {/if}
                <ActionMenu
                  label="More actions"
                  disabled={busyEmail === u.email}
                  items={[{ label: "Delete", onSelect: () => onDelete(u.email), tone: "danger" }]}
                />
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
</Card>
